import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import PageHelmet from '../components/PageHelmet';
import ROUTES from '../routes.json';

const AdminPage = ({ backendUrl }) => {
  // Head handled by PageHelmet
  const [isRunning, setIsRunning] = useState(false);
  const [message, setMessage] = useState(null);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const getAuthHeaders = () => {
    const token = sessionStorage.getItem('google_credential') || '';
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const res = await axios.get(`${backendUrl}/api/admin/users`, { headers: getAuthHeaders() });
      setUsers(res.data.users || []);
    } catch (err) {
      console.error('fetch users error', err);
      setMessage({ ok: false, text: err.response?.data?.error || err.message || 'Failed to load users' });
    } finally {
      setLoadingUsers(false);
    }
  }, [backendUrl]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const triggerNews = async () => {
    setIsRunning(true);
    setMessage(null);
    try {
      const res = await axios.post(`${backendUrl}/api/admin/trigger-news`, {}, { headers: getAuthHeaders() });
      setMessage({ ok: true, text: `Added ${res.data.added || 0} items.` });
    } catch (err) {
      console.error('trigger error', err);
      const text = err.response?.data?.error || err.message || 'Unknown error';
      setMessage({ ok: false, text });
    } finally {
      setIsRunning(false);
    }
  };

  const toggleAdmin = async (googleId, enable) => {
    setMessage(null);
    try {
      const res = await axios.put(`${backendUrl}/api/admin/users/${encodeURIComponent(googleId)}`, { isAdmin: !!enable }, { headers: getAuthHeaders() });
      setMessage({ ok: true, text: `User ${res.data.user.googleId} isAdmin=${res.data.user.isAdmin}` });
      fetchUsers();
    } catch (err) {
      console.error('toggle admin error', err);
      setMessage({ ok: false, text: err.response?.data?.error || err.message || 'Failed to update user' });
    }
  };

  return (
    <div className="admin-page">
      <PageHelmet
        title="Admin Settings & Tools | OneAIHuman"
        description="Administrative settings and operational tools for site maintainers: manage users, trigger scheduled jobs, and inspect system tasks safely."
        canonical={window.location.origin + ROUTES.admin.path}
      />
      <h1 className="page-title">Admin Settings</h1>

      <div className="page-content" style={{ marginTop: 12, color: '#6b7280' }}>
        <p>
          This admin area provides tools for managing site operations, user roles, and scheduled tasks. Admins can inspect known users, toggle administrative privileges, and trigger backend jobs such as the AI news fetcher. These controls are intended for site operators and trusted team members only.
        </p>
        <p>
          Use these controls to maintain content freshness, monitor user activity, and manage permissions. Actions taken here affect live systems; make sure you have the right authorization before updating user roles or triggering jobs.
        </p>
        <ul style={{ marginTop: 8 }}>
          <li>Review and manage registered users and roles.</li>
          <li>Trigger scheduled tasks like news ingestion on-demand.</li>
          <li>Safe, audit-focused actions for operational tasks.</li>
        </ul>
      </div>

      <section className="admin-card">
        <h3>News Fetcher</h3>
        <p>Manually trigger the AI news fetcher. This will run the same job the daily scheduler runs.</p>
        <div style={{display:'flex',gap:12,alignItems:'center'}}>
          <button className="btn primary" onClick={triggerNews} disabled={isRunning}>
            {isRunning ? 'Running…' : 'Trigger News Now'}
          </button>
          {message && (
            <div className={`admin-msg ${message.ok ? 'success' : 'error'}`}>{message.text}</div>
          )}
        </div>
      </section>

      <section className="admin-card" style={{marginTop:20}}>
        <h3>Users</h3>
        <p>List of known users. Toggle admin to grant/revoke admin rights.</p>
        {loadingUsers ? <div>Loading users…</div> : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Avatar</th>
                <th>GoogleId</th>
                <th>Name</th>
                <th>Email</th>
                <th>isPro</th>
                <th>isAdmin</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.googleId}>
                  <td style={{width:56}}>
                    {u.picture ? (
                      <img src={u.picture} alt={u.name || u.googleId} style={{width:32,height:32,borderRadius:16,objectFit:'cover'}} />
                    ) : (
                      <div style={{width:32,height:32,borderRadius:16,background:'#ddd'}} />
                    )}
                  </td>
                  <td style={{maxWidth:400,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{u.googleId}</td>
                  <td>{u.name || '-'}</td>
                  <td style={{maxWidth:220,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{u.email || '-'}</td>
                  <td>{u.isPro ? 'yes' : 'no'}</td>
                  <td>{u.isAdmin ? 'yes' : 'no'}</td>
                  <td>
                    <button className="btn small" onClick={() => toggleAdmin(u.googleId, !u.isAdmin)}>
                      {u.isAdmin ? 'Revoke Admin' : 'Make Admin'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
};

export default AdminPage;
